import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { CircleAvatar } from "@/components/CircleAvatar";
import { startCheckout } from "@/lib/checkout";
import { fireConfetti } from "@/lib/effects";
import { money, tierColor, PRESET_AMOUNTS } from "@/lib/blessed";

// Single "Bless!" flow: 3-2-1 countdown -> random neighbor in radius -> amount popup -> Stripe checkout.
export const QuickBless = ({ open, onOpenChange, people }) => {
  const navigate = useNavigate();
  const [count, setCount] = useState(3);
  const [phase, setPhase] = useState("countdown"); // countdown | pick
  const [target, setTarget] = useState(null);
  const [shuffle, setShuffle] = useState(null);
  const [custom, setCustom] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhase("countdown");
    setCount(3);
    setCustom("");
    const pool = (people || []).filter((p) => !p.isMe);
    const chosen = pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
    setTarget(chosen);
    if (!chosen) return undefined;
    // Suspense: rapidly shuffle avatars while counting down (person stays hidden).
    setShuffle(pool[Math.floor(Math.random() * pool.length)]);
    const shuf = setInterval(() => setShuffle(pool[Math.floor(Math.random() * pool.length)]), 110);
    let n = 3;
    const iv = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(iv);
        clearInterval(shuf);
        // Neighbor revealed — celebrate, then open the full donate page.
        fireConfetti();
        onOpenChange(false);
        navigate(`/donate/${chosen.id}`);
      } else {
        setCount(n);
      }
    }, 800);
    return () => { clearInterval(iv); clearInterval(shuf); };
  }, [open, people]);

  const close = () => onOpenChange(false);

  const send = async (amt) => {
    const value = Number(amt);
    if (!target || !value || value < 5) {
      toast.error("The minimum blessing is $5.");
      return;
    }
    setSending(true);
    try {
      const { url } = await startCheckout({
        recipient_id: target.id,
        amount: value,
        anonymous: false,
      });
      if (url) {
        toast.success("Opening secure checkout…");
        window.location.href = url;
      } else {
        toast.error("Checkout link unavailable.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  // No neighbors to bless
  if (!target) {
    return (
      <div data-testid="quick-bless-overlay" className="fixed inset-0 z-[60] grid place-items-center bg-black/80 px-6" onClick={close}>
        <div className="bg-white text-ink rounded-2xl p-8 max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
          <p className="text-lg font-black text-slate-900">No neighbors nearby right now</p>
          <p className="text-sm text-slate-500 mt-1">Widen your range and try again.</p>
          <button data-testid="quick-bless-close" onClick={close} className="mt-5 h-11 px-6 rounded-pill font-black bg-brand-primary text-white">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quick-bless-overlay" className="fixed inset-0 z-[60] grid place-items-center bg-black/85 backdrop-blur-sm px-5 py-6 overflow-y-auto">
      {phase === "countdown" ? (
        <div className="flex flex-col items-center">
          <div>
            <CircleAvatar src={shuffle?.picture} name={shuffle?.name || "?"} size={88} ring="linear-gradient(135deg,#3B60E8,#8B5CF6)" />
          </div>
          <div key={count} data-testid="quick-bless-count"
            className="mt-6 font-black leading-none animate-pop-in"
            style={{ fontSize: "clamp(120px, 40vw, 240px)", color: "#FFD93D", textShadow: "0 0 40px rgba(255,217,61,0.6)" }}>
            {count}
          </div>
          <p className="text-white/80 font-bold mt-2">Finding someone to bless…</p>
        </div>
      ) : (
        <div data-testid="quick-bless-picker" className="relative bg-white rounded-3xl w-full max-w-[360px] max-h-[90vh] overflow-y-auto p-5 pt-6 animate-slide-up">
          <button data-testid="quick-bless-back" onClick={close}
            className="absolute left-4 top-4 w-9 h-9 rounded-pill grid place-items-center bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div className="flex flex-col items-center">
            <CircleAvatar src={target.picture} name={target.name} size={74} ring="linear-gradient(135deg,#3B60E8,#8B5CF6)" />
            <h2 className="text-2xl font-black text-slate-900 mt-3 text-center">Bless {target.name.split(" ")[0]}</h2>
            <p className="text-slate-500 font-semibold text-sm mt-1">Pick an amount to send</p>
          </div>

          <div className="grid grid-cols-3 gap-2.5 mt-5">
            {PRESET_AMOUNTS.map((a) => (
              <button key={a} data-testid={`quick-bless-amount-${a}`} disabled={sending} onClick={() => send(a)}
                className="py-4 rounded-xl font-black text-white text-lg active:scale-95 transition-transform disabled:opacity-60"
                style={{ background: tierColor(a) }}>
                {money(a)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2.5 mt-3">
            <input data-testid="quick-bless-custom" type="number" inputMode="numeric" value={custom}
              onChange={(e) => setCustom(e.target.value)} placeholder="Custom $"
              className="flex-1 min-w-0 h-12 rounded-xl bg-slate-100 px-4 text-slate-900 font-bold placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-brand-primary" />
            <button data-testid="quick-bless-send" disabled={sending || !custom} onClick={() => send(custom)}
              className="shrink-0 h-12 px-6 rounded-xl font-black text-white bg-brand-primary active:scale-95 transition-transform disabled:opacity-50">
              {sending ? <Loader2 className="animate-spin" size={18} /> : "Send"}
            </button>
          </div>
          <p className="text-center text-slate-400 text-xs mt-3">A small platform fee ($1, or 2.9% over $100) is deducted; the rest goes to them.</p>
        </div>
      )}
    </div>
  );
};
